/**
 * client/hud/hud.ts — the BO2 wager (Sticks and Stones) HUD, drawn on one 2D canvas in LUI
 * virtual units (1280x720, 90 % safe area) [research/hud.md, research/data/hud-layout.json].
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * Every position/size/timing below cites the LUI script (tools/src/t6-lua-dumps/MP/patch_mp/...)
 * or GSC it comes from; engine-drawn parts (crosshair, hit-direction, obituary timing, low-health
 * pulse) come from the T5 KisakBlack reimplementation and are ESTIMATEs for T6, with the T6
 * dvar defaults from research/data/dvars_t6mp.json where they exist.
 *
 * Draw order (back to front): low-health overlay, damage arrows, crosshair, hitmarker, minimap,
 * score/timer cluster, ammo cluster, obituaries, prematch countdown, score popup + feed, medal,
 * connection-interrupted, scoreboard, wager end screen.
 */
import { CONNECTION_INTERRUPTED_MS } from '@shared/constants';
import { getWeaponDef } from '@shared/weapons/defs';
import {
  Button, EF, GameEventType, KillFlags, MatchPhase, Medal, MeansOfDeath, PmType, WeaponId, WeaponState,
  type GameEvent, type ScoreRow,
} from '@shared/types';
import type { ClientWorld, Hud, RefDef } from '../types';
import type { Settings } from '../settings/settings';
import { STR, fmt } from '../ui/strings';
import { COLORS, type RGB, WHITE, drawImage, setAdditiveTarget, uiAssets, type UiAssets } from './gfx';
import { KillFeedQueue, type ObitDrawLine } from './killfeed';
import { HUD640_SCALE, type Rect, type Viewport, anchorRect, computeViewport } from './layout';
import {
  COMPASS_MAX_RANGE, HEADING_STRIP_VISIBLE, MINIMAP_HIJACKED, RadarSweep, compassOffset, compassRotation, headingStripCenterU,
  minimapWorldSize, worldToMinimapUV,
} from './minimap';
import { matchStore } from './matchstore';
import { WAGER_DISPLAY_ANTE, bestOtherScore, placesOf, sortPlacement, topScorers, wagerPayouts } from './wager';

// ---- timings --------------------------------------------------------------------------------
/** _damagefeedback.gsc: alpha 1 then fadeovertime(1) -> 0. */
const HITMARKER_FADE_MS = 1000;
/** cg_hudDamageIconTime 2000 (T5 default, ESTIMATE for T6); 8 slots (T5 viewDamage[8]). */
const DAMAGE_ICON_MS = 2000;
/** CoD.ScorePopup.DisplayTime (scorepopup.lua:5); AdditiveTextOverlay pulse in 500 / out 250. */
const SCORE_POPUP_MS = 1000;

/**
 * Score popup glow at time t (ms) for a label textW wide (additivetextoverlay.lua + scorepopup.lua).
 * - outer glow textW*2.5 x 30*4: "initial" white -> "default" scoreFeedColor1 over PulseInTime 500 ms;
 * - inner glow half that size, cycling Color2 <-> Color3 every ColorShiftTime 500 ms.
 * Ends at DisplayTime 1000 ms with NO pulse-out: resetAndCloseAfterTime() targets popup.glow / popup.label,
 * which are nil on the ScorePopup, so LUI.UITimer.Tick sends "out" and "close" to the timers' parent
 * (centerScoreNumber), whose default "close" handler (luielement.lua m_eventHandlers) removes the whole
 * widget in the same frame. The 250 ms AdditiveTextOverlay.Out flash is therefore never visible.
 */
/** scoreboard.lua: ping bar image index 1..4 = max(1, #bars - floor(ping / 100)). */
export function pingBars(ping: number): number {
  return Math.max(1, Math.min(4, 4 - Math.floor(Math.max(0, ping) / 100)));
}

export function scorePopupGlow(t: number, textW: number): { outer: { w: number; h: number; col: RGB }; inner: { w: number; h: number; col: RGB } } | null {
  if (t < 0 || t >= SCORE_POPUP_MS) return null;
  const w = textW * 2.5, h = 30 * 4;
  const outer = mix(WHITE, COLORS.scoreFeed1, Math.min(1, t / GLOW_PULSE_IN_MS));
  const c = (t % (2 * GLOW_COLOR_SHIFT_MS)) / GLOW_COLOR_SHIFT_MS;
  const inner = c < 1 ? mix(COLORS.scoreFeed2, COLORS.scoreFeed3, c) : mix(COLORS.scoreFeed3, COLORS.scoreFeed2, c - 1);
  return { outer: { w, h, col: outer }, inner: { w: w * 0.5, h: h * 0.5, col: inner } };
}
const GLOW_PULSE_IN_MS = 500;
const GLOW_COLOR_SHIFT_MS = 500;
/** CoD.ScoreFeed (scorefeed.lua): rows 25 tall (CoD.textSize.Default), expand 200, max 4, clear 1000 after the last. */
const FEED_ROW_H = 25;
const FEED_EXPAND_MS = 200;
const FEED_MAX_ROWS = 4;
const FEED_DURATION_MS = 1000;
/** notificationpopups.lua:10-16 MedalInTime 200, MedalVisibleTime 2000, MedalOutTime 250. */
const MEDAL_IN_MS = 200;
const MEDAL_TOTAL_MS = 2000;
const MEDAL_OUT_MS = 250;
/** scorebottomleft.lua: "toggle_winning_status" every 9000 ms, fade out/in 500 ms. */
const WIN_STATUS_TOGGLE_MS = 9000;
const WIN_STATUS_FADE_MS = 500;
const WIN_STATUS_ALPHA = 0.65;
/** ammoarea.lua: WeaponSelectionDelay 2000, WeaponSelectionDuration 100 (fire-rate fades in over 2x that). */
const WEAPON_LABEL_MS = 2000;
const WEAPON_LABEL_SLIDE_MS = 100;
/** huddigit.lua pulse_red 500 ms each way. */
const DIGIT_PULSE_MS = 500;
/** Game timer turns BOIIOrange on "countdown_low" (gametimer.lua). Threshold: ESTIMATE 30 s. */
const TIMER_LOW_MS = 30_000;
/** hud health overlay dvars (t6mp dvar dump). */
const HO = {
  pulseStart: 0.66, pulseStop: 0.9, regenPauseTime: 8000,
  phaseOneDur: 150, phaseOneAlphaAdd: 0.3, phaseTwoDur: 320, phaseTwoMul: 1, phaseThreeDur: 400, phaseThreeMul: 1,
  phaseEndDur: 700, phaseEndTo: 0,
};
/** cg_crosshairAlpha 1, cg_crosshairAlphaMin 0.5 (t6mp dvar dump). */
const CROSSHAIR_ALPHA_MIN = 0.5;
/** hudelem font height at 720p per unit fontscale: IW fontScale 1 = 12 px at 640x480 -> 18 px (ESTIMATE). */
const HUDELEM_PX = 12 * HUD640_SCALE;
/** CoD.ScoreFeed.LabelOffset etc. are inside the safe area; the score popup is full screen. */
const SCORE_TEXT_TOP = -75; // CoD.ScorePopup.ScoreTextTop
/** gamesettings_sas.cfg timelimit 5 [SAS §1]. */
const SNS_TIME_LIMIT_MS = 5 * 60_000;

// ---- weapon HUD data ------------------------------------------------------------------------
interface WeaponHud {
  name: string; //            localised display name
  showAmmo: boolean; //       hidden for melee / grenade types (ammoarea.lua UpdateWeapon)
  fireTypeIcon: string; //    weapon/hud_mp_firerate_*.png or ''
  reticleSide: string;
  reticleSideSize: number;
  reticleCenter: string;
  reticleCenterSize: number;
  reticleMinOfs: number;
  hipReticleSidePos: number;
  adsCrosshairInFrac: number;
  adsCrosshairOutFrac: number;
  lowAmmo: number;
  clipSize: number;
}
const weaponHudCache = new Map<number, WeaponHud>();
function weaponHud(id: number): WeaponHud {
  let w = weaponHudCache.get(id);
  if (w) return w;
  const d = getWeaponDef(id as WeaponId);
  const r = d.raw as Record<string, unknown>;
  const s = (k: string) => (typeof r[k] === 'string' ? (r[k] as string) : '');
  const n = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : 0);
  const disp = s('displayName');
  w = {
    name: ((STR as Record<string, string>)[disp] ?? d.displayNameEnglish ?? '').toUpperCase(),
    showAmmo: !(d.weaponType === 'melee' || d.weaponType === 'grenade' || d.weaponType === 'riotshield'),
    fireTypeIcon: s('fireTypeIcon') ? `weapon/${s('fireTypeIcon')}.png` : '',
    reticleSide: s('reticleSide') ? `crosshair/${s('reticleSide')}.png` : '',
    reticleSideSize: n('reticleSideSize'),
    reticleCenter: s('reticleCenter') ? `crosshair/${s('reticleCenter')}.png` : '',
    reticleCenterSize: n('reticleCenterSize'),
    reticleMinOfs: n('reticleMinOfs'),
    hipReticleSidePos: n('hipReticleSidePos'),
    adsCrosshairInFrac: n('adsCrosshairInFrac'),
    adsCrosshairOutFrac: n('adsCrosshairOutFrac'),
    lowAmmo: n('lowAmmoWarningThreshold'),
    clipSize: d.clipSize,
  };
  weaponHudCache.set(id, w);
  return w;
}

/** Obituary icon per kill [hud.md "Kill icons"; T5 CG_Obituary mod overrides]. */
export function obituaryIcon(weapon: number, mod: number, flags: number): { path: string; flip: boolean } {
  if (mod === MeansOfDeath.FALLING) return { path: 'obituary/hud_obit_death_falling.png', flip: false };
  if (mod === MeansOfDeath.CRUSH) return { path: 'obituary/hud_obit_death_crush.png', flip: false };
  if (mod === MeansOfDeath.SUICIDE) return { path: 'obituary/hud_obit_death_suicide.png', flip: false };
  if (mod === MeansOfDeath.MELEE) return { path: 'obituary/hud_obit_knife.png', flip: false };
  if (mod === MeansOfDeath.HEAD_SHOT || (flags & KillFlags.HEADSHOT)) return { path: 'obituary/killiconheadshot.png', flip: false };
  if (mod === MeansOfDeath.TRIGGER_HURT || mod === MeansOfDeath.DROWN || weapon === WeaponId.NONE) return { path: 'obituary/killicondied.png', flip: false };
  // explosive bolt blast: explosive_bolt_mp killIcon = hud_obit_exploding_arrow
  if (weapon === WeaponId.EXPLOSIVE_BOLT || (weapon === WeaponId.CROSSBOW && (mod === MeansOfDeath.GRENADE_SPLASH || mod === MeansOfDeath.EXPLOSIVE || mod === MeansOfDeath.PROJECTILE_SPLASH))) {
    return { path: 'obituary/hud_obit_exploding_arrow.png', flip: false };
  }
  const raw = getWeaponDef(weapon as WeaponId).raw as Record<string, unknown>;
  const icon = typeof raw['killIcon'] === 'string' ? (raw['killIcon'] as string) : '';
  const flip = raw['flipKillIcon'] === 1;
  if (!icon) return { path: 'obituary/hud_obit_death_suicide.png', flip: false };
  const dir = icon.startsWith('menu_mp_weapons') ? 'weapon' : 'obituary';
  return { path: `${dir}/${icon}.png`, flip };
}

interface ObitData { attacker: string; victim: string; icon: string; flip: boolean }

interface MedalInfo { image: string; name: string }
/** scoreinfo.csv columns 2/3 (name / material) [hud.md "SnS medals"]. */
export const MEDALS: Record<number, MedalInfo> = {
  [Medal.BANKRUPTED]: { image: 'medal/hud_medals_sticks_bankrupt.png', name: STR.MEDAL_KILL_WITH_AXE_SAS },
  [Medal.REGICIDE]: { image: 'medal/hud_medals_sticks_regicide.png', name: STR.MEDAL_KILL_LEADER_WITH_AXE_SAS },
  [Medal.SWITCH_HITTER]: { image: 'medal/hud_medals_sticks_switchhitter.png', name: STR.MEDAL_KILL_WITH_CROSSBOW_AND_BALLISTIC_SAS },
  // scoreinfo first_kill / hatchet bounce kill (MEDAL_FIRST_KILL, MEDAL_BOUNCE_HATCHET_KILL)
  [Medal.FIRST_BLOOD]: { image: 'medal/hud_medals_firstblood.png', name: STR.MEDAL_FIRST_KILL },
  [Medal.BANKSHOT]: { image: 'medal/hud_medals_bankshot.png', name: STR.MEDAL_BOUNCE_HATCHET_KILL },
  // (integration, CONTRACT-REQUESTS #G4) general score-event medals: art/name = scoreinfo.csv columns 3/2
  [Medal.HEADSHOT]: { image: 'medal/hud_medals_headshot.png', name: STR.MEDAL_HEADSHOT },
  [Medal.ASSISTED_SUICIDE]: { image: 'medal/hud_medals_assist_suicide.png', name: STR.MEDAL_ASSISTED_SUICIDE },
  [Medal.REVENGE]: { image: 'medal/hud_medals_revenge.png', name: STR.MEDAL_REVENGE_KILL },
  [Medal.SKEWERED]: { image: 'medal/hud_medals_skewer.png', name: STR.MEDAL_BALLISTIC_KNIFE_KILL },
  [Medal.LONGSHOT]: { image: 'medal/hud_medals_longshot.png', name: STR.MEDAL_LONGSHOT_KILL },
  [Medal.BACKSTABBER]: { image: 'medal/hud_medals_backstabber.png', name: STR.MEDAL_BACKSTABBER_KILL },
  [Medal.CLOSE_CALL]: { image: 'medal/hud_medals_closecall.png', name: STR.MEDAL_KILL_ENEMY_WHEN_INJURED },
  [Medal.AFTERLIFE]: { image: 'medal/hud_medals_afterlife.png', name: STR.MEDAL_KILL_ENEMY_AFTER_DEATH },
  [Medal.COMEBACK]: { image: 'medal/hud_medals_comeback.png', name: STR.MEDAL_COMEBACK_FROM_DEATHSTREAK },
  [Medal.DOUBLE_KILL]: { image: 'medal/hud_medals_doublekill.png', name: STR.MEDAL_MULTIKILL_2 },
  [Medal.TRIPLE_KILL]: { image: 'medal/hud_medals_triplekill.png', name: STR.MEDAL_MULTIKILL_3 },
  [Medal.QUAD_KILL]: { image: 'medal/hud_medals_quadkill.png', name: STR.MEDAL_MULTIKILL_4 },
  [Medal.FRENZY_KILL]: { image: 'medal/hud_medals_frenzykill.png', name: STR.MEDAL_MULTIKILL_5 },
  [Medal.SUPER_KILL]: { image: 'medal/hud_medals_superkill.png', name: STR.MEDAL_MULTIKILL_6 },
  [Medal.MEGA_KILL]: { image: 'medal/hud_medals_megakill.png', name: STR.MEDAL_MULTIKILL_7 },
  [Medal.ULTRA_KILL]: { image: 'medal/hud_medals_ultrakill.png', name: STR.MEDAL_MULTIKILL_8 },
  [Medal.KILL_CHAIN]: { image: 'medal/hud_medals_killchain.png', name: STR.MEDAL_MULTIKILL_MORE_THAN_8 },
  [Medal.UNINTERRUPTED_OBIT]: { image: 'medal/hud_medals_fourpiece.png', name: STR.MEDAL_UNINTERRUPTED_OBIT_FEED_KILLS },
};

interface FeedRow { text: string; start: number }
interface DamageSlot { yaw: number; time: number }

/** Preloaded art (so the first popup does not draw blank). */
export const PRELOAD = [
  'hud/hud_mp_vis_left_lower_back.png', 'hud/hud_mp_vis_left_lower.png', 'hud/hud_mp_vis_right_lower.png',
  'hud/hud_mp_vis_right_lower_white.png', 'hud/hud_mp_vis_gun_title_left.png', 'hud/hud_mp_vis_gun_title_mid.png',
  'hud/hud_mp_vis_gun_title_right.png', 'hud/ks_menu_background.png',
  ...Array.from({ length: 10 }, (_, i) => `hud/hud_mp_num_big_${i}.png`),
  ...Array.from({ length: 10 }, (_, i) => `hud/hud_mp_num_big_${i}_white.png`),
  'hud/hud_mp_num_big_slash.png', 'hud/hud_mp_num_big_slash_white.png',
  'compass/compass_map_mp_hijacked.png', 'compass/compassping_player.png', 'compass/compassping_enemy.png',
  'compass/compassping_firstplace.png', 'compass/hud_horizontal_compass_minimap.png', 'compass/mp_minimap_overlay.png',
  'compass/compass_map_color_underlay.png',
  'weapon/hud_hatchet.png', 'weapon/hud_mp_firerate_bolt.png', 'weapon/hud_mp_firerate_free.png',
  'weapon/menu_mp_weapons_crossbow.png', 'weapon/menu_mp_weapons_ballistic_knife.png',
  'crosshair/reticle_side_small.png', 'crosshair/knife_ballistic_reticle.png', 'crosshair/hatchet_reticle.png',
  'hitmarker/damage_feedback.png', 'damage/hit_direction.png', 'damage/overlay_low_health.png',
  'obituary/hud_obit_hatchet.png', 'obituary/hud_obit_knife.png', 'obituary/hud_obit_exploding_arrow.png',
  'obituary/killiconheadshot.png', 'obituary/hud_obit_death_suicide.png', 'obituary/hud_obit_death_falling.png',
  'obituary/killicondied.png',
  'medal/hud_lui_scorefeed_glow.png', 'medal/hud_lui_medal_glow.png', 'misc/emblem_bg_default.png', 'medal/hud_medals_sticks_bankrupt.png',
  'medal/hud_medals_sticks_regicide.png', 'medal/hud_medals_sticks_switchhitter.png',
  'scoreboard/ping_bar_01.png', 'scoreboard/ping_bar_02.png', 'scoreboard/ping_bar_03.png', 'scoreboard/ping_bar_04.png',
  'menu/menu_mp_cac_grad_stretch.png', 'wager/playlist_sticks_and_stones.png',
  'wager/menu_mp_lobby_aar_wager01.png', 'wager/menu_mp_lobby_aar_wager02.png', 'wager/menu_mp_lobby_aar_wager03.png',
];

export class Bo2Hud implements Hud {
  private root: HTMLDivElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  /**
   * Three stacked layers keep the engine's draw order with additive art: `under` (alpha art drawn
   * before additive art: back panels, low-health overlay), the additive layer (mix-blend-mode:
   * plus-lighter, gfx.ts setAdditiveTarget) and the top layer (`ctx`: digits, text, icons).
   */
  private under: CanvasRenderingContext2D | null = null;
  private addCtx: CanvasRenderingContext2D | null = null;
  private layers: HTMLCanvasElement[] = [];
  private visible = true;
  private readonly a: UiAssets = uiAssets();
  private vp: Viewport = computeViewport(1280, 720);
  private dpr = 1;
  /** Client clock (ms), advanced by update(dtMs). */
  private t = 0;

  // state
  private readonly obits = new KillFeedQueue<ObitData>();
  private readonly obitDraw: ObitDrawLine<ObitData>[] = [];
  private hitmarkerTime = -1e9;
  private readonly damage: DamageSlot[] = [];
  private scorePopup: { text: string; start: number; count: number } | null = null;
  private feed: FeedRow[] = [];
  private feedLast = -1e9;
  private readonly medalQueue: Medal[] = [];
  private medal: { info: MedalInfo; start: number } | null = null;
  private splash: { title: string; sub: string; start: number } | null = null;
  private lastWeapon = -1;
  private weaponSelectTime = -1e9;
  private winStatusToggle = 0;
  private winStatusShow = true;
  private readonly radar = new RadarSweep();
  private radarPings: { x: number; y: number; first: boolean }[] = [];
  private lastAds = 0;
  private adsIn = false;
  private prematchSec = -1;
  private prematchPulse = -1e9;
  private postgameStart = -1;
  private keyScoreboard = false;
  /** Obituary callout nameplate (notificationpopups.lua PlayerObituaryCallout). */
  private callout: { text: string; name: string; start: number } | null = null;
  /** KILLCAM event (#G1): letterbox overlay while the replay runs. */
  private killcam: { start: number; dur: number; attacker: number; weapon: number; final: boolean } | null = null;
  private lastSpawnCount = -1;
  // low-health overlay (CG_PulseLowHealthOverlay state)
  private ho = { oldHealth: 1, hurt: false, pulseTime: 0, pulseDuration: 0, fromAlpha: 0, toAlpha: 0, pulseIndex: 0, phase: 0, lastHitTime: -1e9 };

  private readonly onKey = (e: KeyboardEvent) => {
    // Fallback scoreboard key (the scoreboard normally follows ps.oldButtons & Button.SCOREBOARD, #H1).
    if (e.code !== 'Tab') return;
    this.keyScoreboard = e.type === 'keydown' && this.visible;
    if (this.visible && e.type === 'keydown') e.preventDefault();
  };
  private readonly onBlur = () => { this.keyScoreboard = false; };

  mount(parent: HTMLElement): void {
    const root = document.createElement('div');
    root.className = 'hud';
    const mk = (cls: string) => { const c = document.createElement('canvas'); c.className = cls; root.appendChild(c); return c; };
    const under = mk('hud-canvas hud-canvas-under');
    const add = mk('hud-canvas hud-canvas-add');
    const canvas = mk('hud-canvas hud-canvas-top');
    parent.appendChild(root);
    this.root = root;
    this.layers = [under, add, canvas];
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.under = under.getContext('2d');
    this.addCtx = add.getContext('2d');
    if (this.ctx && this.addCtx) setAdditiveTarget(this.ctx, this.addCtx);
    if (this.under && this.addCtx) setAdditiveTarget(this.under, this.addCtx);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKey);
    window.addEventListener('blur', this.onBlur);
    void this.a.loadAllFonts();
    void this.a.preload(PRELOAD);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    if (this.root) this.root.style.display = v ? '' : 'none';
    if (!v) this.resetMatchState();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKey);
    window.removeEventListener('blur', this.onBlur);
    this.root?.remove();
    this.root = null;
  }

  private resetMatchState(): void {
    this.obits.clear();
    this.damage.length = 0;
    this.scorePopup = null;
    this.feed = [];
    this.medalQueue.length = 0;
    this.medal = null;
    this.splash = null;
    this.callout = null;
    this.killcam = null;
    this.radar.reset();
    this.radarPings = [];
    this.postgameStart = -1;
    this.lastWeapon = -1;
  }

  // =========================================================================================
  update(world: ClientWorld, refdef: RefDef, _settings: Settings, dtMs: number): void {
    this.t += Math.max(0, dtMs);
    const now = this.t;
    for (const fe of world.frameEvents) if (fe.src === 'game') this.onGameEvent(world, fe.ge, now);
    matchStore.update(world);
    if (!this.visible || !this.canvas || !this.ctx) return;
    this.resize();
    const s = this.vp.scale * this.dpr;
    for (const c of [this.under, this.addCtx, this.ctx]) {
      if (!c) continue;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.clearRect(0, 0, c.canvas.width, c.canvas.height);
      c.setTransform(s, 0, 0, s, 0, 0);
      c.imageSmoothingEnabled = true;
    }
    this.draw(world, refdef, now);
  }

  private resize(): void {
    const c = this.canvas!;
    const w = c.clientWidth || window.innerWidth, h = c.clientHeight || window.innerHeight;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const pw = Math.round(w * dpr), ph = Math.round(h * dpr);
    for (const l of this.layers) if (l.width !== pw || l.height !== ph) { l.width = pw; l.height = ph; }
    this.dpr = dpr;
    if (this.vp.vw * this.vp.scale !== w || this.vp.vh * this.vp.scale !== h) this.vp = computeViewport(w, h);
  }

  // =========================================================================================
  // events
  // =========================================================================================
  private onGameEvent(world: ClientWorld, ge: GameEvent, now: number): void {
    const me = world.localClientNum;
    switch (ge.type) {
      case GameEventType.KILL: {
        const icon = obituaryIcon(ge.weapon, ge.mod, ge.flags);
        const victim = this.nameOf(world, ge.victim);
        // T5 CG_Obituary: suicide / world kill -> attacker name blank
        const suicide = ge.killer === ge.victim || ge.killer < 0 || (ge.flags & KillFlags.SUICIDE) !== 0;
        const attacker = suicide || !world.playerInfo.has(ge.killer) ? '' : this.nameOf(world, ge.killer);
        this.obits.add({ attacker, victim, icon: icon.path, flip: icon.flip }, now);
        // T5 CG_Obituary -> CG_DisplayPlayerCard: the killer sees the victim's card ("Killed"), the
        // victim sees the killer's ("Killed By"); BO2 draws it as the obituary callout nameplate
        if (!suicide && attacker && ge.killer === me) this.callout = { text: STR.CGAME_KILLED, name: victim, start: now };
        else if (!suicide && attacker && ge.victim === me) this.callout = { text: STR.CGAME_KILLEDBY, name: attacker, start: now };
        break;
      }
      case GameEventType.HITMARKER:
        this.hitmarkerTime = now;
        break;
      case GameEventType.DAMAGE_TAKEN: {
        // T5 viewDamage[8]: reuse the oldest slot
        if (this.damage.length >= 8) this.damage.shift();
        // (integration, #H1.6 / #W3) combat sends the damage TRAVEL yaw (attacker -> victim); the arc needs the
        // yaw from us toward the attacker
        this.damage.push({ yaw: ge.dirYaw + 180, time: now });
        break;
      }
      case GameEventType.SCORE_POPUP: {
        // CoD.ScorePopup.Score: ScoreCount accumulates until the popup times out (1000 ms)
        const live = this.scorePopup && now - this.scorePopup.start < SCORE_POPUP_MS;
        const count = (live ? this.scorePopup!.count : 0) + ge.amount;
        if (count !== 0) this.scorePopup = { text: count > 0 ? STR.MP_PLUS + count : String(count), start: now, count };
        break;
      }
      case GameEventType.MEDAL: {
        if (MEDALS[ge.medal]) {
          this.medalQueue.push(ge.medal);
          // the score feed lists medal / event names (not plain kills)
          this.addFeedRow(MEDALS[ge.medal]!.name, now);
        }
        break;
      }
      case GameEventType.BANKRUPT: {
        // sas.gsc onplayerkilled: only the self-bankrupt branch calls queuewagerpopup(MP_HUMILIATED, 0,
        // MP_BANKRUPTED). BO2's _popups.gsc never reads wagernotifyqueue, so in the shipped game this
        // splash is probably not drawn at all; we show it (BO1-style wager popup) for the
        // self-bankrupt case only - ESTIMATE, see research/hud.md "Victim's bankrupt message".
        const selfInflicted = ge.attacker === ge.victim || ge.attacker < 0;
        if (ge.victim === me && selfInflicted) this.splash = { title: STR.MP_HUMILIATED, sub: STR.MP_BANKRUPTED, start: now };
        break;
      }
      case GameEventType.KILLCAM:
        if (ge.victim === me || ge.final) this.killcam = { start: now, dur: ge.camMs + ge.postMs, attacker: ge.attacker, weapon: ge.weapon, final: ge.final };
        break;
      case GameEventType.SPAWN:
        if (ge.ent === me) this.killcam = null;
        if (ge.ent === me) { this.damage.length = 0; this.ho.hurt = false; this.ho.toAlpha = 0; this.ho.fromAlpha = 0; }
        break;
      default:
        break;
    }
  }

  private addFeedRow(text: string, now: number): void {
    this.feed.push({ text, start: now });
    if (this.feed.length > FEED_MAX_ROWS) this.feed.shift();
    this.feedLast = now;
  }

  private nameOf(world: ClientWorld, cn: number): string {
    return world.playerInfo.get(cn)?.name ?? `Player ${cn}`;
  }

  // =========================================================================================
  // drawing
  // =========================================================================================
  private draw(world: ClientWorld, refdef: RefDef, now: number): void {
    const ctx = this.ctx!;
    const ps = world.predicted;
    const phase = world.phase;
    const ended = phase >= MatchPhase.POSTGAME;
    if (ended && this.postgameStart < 0) this.postgameStart = now;
    if (!ended) this.postgameStart = -1;
    const dead = ps.pm_type === PmType.DEAD || (ps.eFlags & EF.DEAD) !== 0 || ps.health <= 0;
    const scoreboardOpen = !ended && (this.keyScoreboard || (ps.oldButtons & Button.SCOREBOARD) !== 0);

    if (ps.spawnCount !== this.lastSpawnCount) { this.lastSpawnCount = ps.spawnCount; this.lastWeapon = -1; }

    if (this.killcam && now - this.killcam.start >= this.killcam.dur) this.killcam = null;
    if (this.killcam || phase === MatchPhase.FINAL_KILLCAM) {
      // killcam.lua: the regular HUD is hidden (BIT_IN_KILLCAM); obituaries are hidden too
      this.drawKillcam(ctx, world, phase === MatchPhase.FINAL_KILLCAM || !!this.killcam?.final);
      return;
    }
    if (!ended) {
      this.drawLowHealth(this.under ?? ctx, ps.health, ps.maxHealth, dead, now);
      if (!dead) this.drawDamageArrows(ctx, world.view.viewangles[1], now);
      if (!dead && !scoreboardOpen) this.drawCrosshair(ctx, world, refdef);
      this.drawHitmarker(ctx, now);
      if (!scoreboardOpen) {
        this.drawMinimap(ctx, world, now);
        this.drawScoreCluster(ctx, world, now);
        if (!dead) this.drawAmmoCluster(ctx, world, now);
      }
      this.drawObituaries(ctx, now);
      this.drawCallout(ctx, now);
      this.drawPrematch(ctx, world, now);
      this.drawScorePopup(ctx, now);
      this.drawMedal(ctx, now);
      this.drawSplash(ctx, now);
      if ((ps.eFlags & EF.CONNECTION_INTERRUPTED) !== 0) this.drawConnectionInterrupted(ctx, now);
      if (scoreboardOpen) this.drawScoreboard(ctx, world);
    } else {
      this.drawWagerOutcome(ctx, world, now - this.postgameStart);
    }
  }

  // ---- bitmap digit (huddigit.lua): foreground + offWhite/0.6 "_white" layer on top ----------
  private digit(ctx: CanvasRenderingContext2D, d: string, x: number, y: number, w: number, h: number, pulse: number): void {
    const tint: RGB | undefined = pulse > 0 ? [1, 1 - pulse, 1 - pulse] : undefined;
    drawImage(ctx, this.a, `hud/hud_mp_num_big_${d}.png`, x, y, w, h, { tint });
    drawImage(ctx, this.a, `hud/hud_mp_num_big_${d}_white.png`, x, y, w, h, { alpha: 0.6, tint });
  }

  /** "pulse_red" over 500 ms and back (huddigit.lua f0_local4/5/6): 0..1 red amount. */
  private redPulse(now: number): number {
    const p = (now % (2 * DIGIT_PULSE_MS)) / DIGIT_PULSE_MS;
    return p < 1 ? p : 2 - p;
  }

  // ---- score / timer cluster (scorebottomleft.lua) -----------------------------------------
  private drawScoreCluster(ctx: CanvasRenderingContext2D, world: ClientWorld, now: number): void {
    const sa = this.vp.safe;
    const base = 28; // f0_local4
    let r = anchorRect(sa, 'left', base, base + 300, 'bottom', -150, 0);
    drawImage(this.under ?? ctx, this.a, 'hud/hud_mp_vis_left_lower_back.png', r.x, r.y, r.w, r.h);
    r = anchorRect(sa, 'left', base - 25, base - 25 + 300, 'bottom', -128, -128 + 150);
    drawImage(ctx, this.a, 'hud/hud_mp_vis_left_lower.png', r.x, r.y, r.w, r.h);

    // game timer: Italic 25, left at base+8, vertically centred on -87; BOIIOrange on countdown_low
    // during the prematch countdown timeLeftMs counts the countdown; the HUD timer shows the full
    // time limit (sas timelimit 5 [SAS §1]) until the match starts
    const tl = world.phase < MatchPhase.PLAYING ? (world.timeLimitMs || SNS_TIME_LIMIT_MS) : Math.max(0, world.timeLeftMs);
    const low = world.phase === MatchPhase.PLAYING && tl <= TIMER_LOW_MS;
    const secs = Math.ceil(tl / 1000);
    const timerText = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
    const ty = sa.y + sa.h - 87 - 25 / 2;
    this.a.text(ctx, 'Italic', timerText, sa.x + base + 8, ty, { color: low ? COLORS.BOIIOrange : WHITE });

    // scores: you vs best other (FFA "enemyScore")
    const me = world.scoreboard.find((x) => x.clientNum === world.localClientNum);
    const yourScore = me?.points ?? 0;
    const enemyScore = bestOtherScore(world.scoreboard, world.localClientNum);
    const status = enemyScore < yourScore ? STR.MENU_WINNING_CAPS : yourScore < enemyScore ? STR.MENU_LOSING_CAPS : STR.MENU_TIED_CAPS;
    // sas scorelimit 0: no win-condition text, so the label alternates status / blank every 9 s
    if (now - this.winStatusToggle >= WIN_STATUS_TOGGLE_MS) { this.winStatusToggle = now; this.winStatusShow = !this.winStatusShow; }
    const sinceToggle = now - this.winStatusToggle;
    let wsAlpha: number;
    let showStatus = this.winStatusShow;
    if (sinceToggle < WIN_STATUS_FADE_MS && now > WIN_STATUS_TOGGLE_MS) {
      showStatus = !this.winStatusShow; // still fading out the previous text
      wsAlpha = WIN_STATUS_ALPHA * (1 - sinceToggle / WIN_STATUS_FADE_MS);
    } else if (sinceToggle < 2 * WIN_STATUS_FADE_MS && now > WIN_STATUS_TOGGLE_MS) {
      wsAlpha = WIN_STATUS_ALPHA * ((sinceToggle - WIN_STATUS_FADE_MS) / WIN_STATUS_FADE_MS);
    } else wsAlpha = WIN_STATUS_ALPHA;
    if (showStatus) this.a.text(ctx, 'SmallItalic', status, sa.x + base + 52, sa.y + sa.h - 96, { alpha: wsAlpha });

    // FFA emblem 64x64 at base+40, centred on -39 (UpdateTeamChange else-branch). Emblems are
    // generated online: stand-in = the SnS playlist icon (deviation: hud.md suggests the 4:1
    // emblem background, which would squash into a square).
    r = anchorRect(sa, 'left', base + 40, base + 40 + 64, 'bottom', -39 - 32, -39 + 32);
    drawImage(ctx, this.a, 'wager/playlist_sticks_and_stones.png', r.x, r.y, r.w, r.h);

    // your score: big digits at base+110, y -87..-23, step 22
    this.digits(ctx, yourScore, sa.x + base + 110, sa.y + sa.h - 87, 1, 0);
    // leader score: 0.75 scale at base+107, y -48+4
    this.digits(ctx, enemyScore, sa.x + base + 107, sa.y + sa.h - 48 + 4, 0.75, 0);
  }

  /** UpdateScoreDigits: up to 5 digits, leading zeros suppressed, left to right. */
  private digits(ctx: CanvasRenderingContext2D, value: number, x: number, y: number, scale: number, pulse: number): void {
    const str = String(Math.max(0, Math.min(99999, Math.trunc(value))));
    const w = 32 * scale, h = 64 * scale, step = 22 * scale;
    for (const ch of str) { this.digit(ctx, ch, x, y, w, h, pulse); x += step; }
  }

  // ---- ammo cluster (ammoarea.lua) ----------------------------------------------------------
  private drawAmmoCluster(ctx: CanvasRenderingContext2D, world: ClientWorld, now: number): void {
    const sa = this.vp.safe;
    const ps = world.predicted;
    const right = -28, bottom = -7; // CoD.AmmoArea.Right / Bottom
    let r = anchorRect(sa, 'right', right + 25 - 300, right + 25, 'bottom', 22 - 150, 22);
    drawImage(this.under ?? ctx, this.a, 'hud/hud_mp_vis_right_lower_white.png', r.x, r.y, r.w, r.h);
    drawImage(ctx, this.a, 'hud/hud_mp_vis_right_lower.png', r.x, r.y, r.w, r.h);

    const weapon = ps.weapon;
    if (weapon !== this.lastWeapon) { this.lastWeapon = weapon; this.weaponSelectTime = now; }
    const wh = weaponHud(weapon);
    const R = sa.x + sa.w, B = sa.y + sa.h;

    // ammo digits right-to-left from Right-20: stock (small), slash (small), clip (big, pulses when low)
    if (weapon !== WeaponId.NONE && wh.showAmmo) {
      const clip = ps.ammoClip[weapon] ?? 0, stock = ps.ammoStock[weapon] ?? 0;
      const lowClip = wh.clipSize > 0 && clip / wh.clipSize <= wh.lowAmmo;
      const pulse = lowClip ? this.redPulse(now) : 0;
      let x = R + right - 20;
      const sw = 32 * 0.75, sh = 64 * 0.75, sstep = 22 * 0.75, sy = B - 87 + 12;
      for (const ch of String(Math.min(999, stock)).split('').reverse()) { this.digit(ctx, ch, x - sw, sy, sw, sh, 0); x -= sstep; }
      this.digit(ctx, 'slash', x - sw, sy, sw, sh, 0); x -= sstep;
      for (const ch of String(Math.min(999, clip)).split('').reverse()) { this.digit(ctx, ch, x - 32, B - 87, 32, 64, pulse); x -= 22; }
    }

    // lethal: one hud_hatchet per axe, right-anchored at Right-40, 24 px step; alpha 0.75^(n-1) rising leftwards
    const lethal = ps.offhandPrimary !== WeaponId.NONE ? ps.offhandPrimary : WeaponId.HATCHET;
    const nAxes = (ps.weaponsOwned & (1 << lethal)) !== 0 || ps.ammoClip[lethal]! > 0 ? Math.min(4, ps.ammoClip[lethal] ?? 0) : 0;
    let a = Math.pow(0.75, nAxes - 1);
    let lx = R + right - 40;
    for (let i = 0; i < nAxes; i++) {
      drawImage(ctx, this.a, 'weapon/hud_hatchet.png', lx - 24, B + bottom - 24, 24, 24, { alpha: a });
      lx -= 24; a /= 0.75;
    }

    // weapon-name tab + fire-rate badge (UpdateWeaponSelect / SwitchToCustomized)
    if (weapon !== WeaponId.NONE && wh.name) {
      const since = now - this.weaponSelectTime;
      const textW = this.a.measure('SmallItalic', wh.name);
      const extra = Math.max(0, textW - 18);
      const fullW = extra + 128, visW = extra + 30, trim = (fullW - visW) / 2;
      // stencil container: right edge at R-30, left at R-590, y B-118..B-54
      const stencilR = R + right + -15 + 58 + -45;
      let off = trim; // right offset of the label inside the stencil
      let fireA = 0;
      if (since >= WEAPON_LABEL_MS && wh.fireTypeIcon) {
        const k = Math.min(1, (since - WEAPON_LABEL_MS) / WEAPON_LABEL_SLIDE_MS);
        off = trim + (trim - 64 - trim) * k; // slides to (-64 + trim)
        fireA = Math.min(1, (since - WEAPON_LABEL_MS) / (WEAPON_LABEL_SLIDE_MS * 2));
      }
      const y0 = B - 54 - 64;
      const x1 = stencilR + off, x0 = x1 - fullW;
      const clip: [number, number, number, number] = [R - 590, y0, stencilR - (R - 590), 64];
      ctx.save();
      ctx.beginPath();
      ctx.rect(clip[0], clip[1], clip[2], clip[3]);
      ctx.clip();
      drawImage(ctx, this.a, 'hud/hud_mp_vis_gun_title_left.png', x0, y0, 64, 64, { clip });
      drawImage(ctx, this.a, 'hud/hud_mp_vis_gun_title_mid.png', x0 + 64, y0, fullW - 128, 64, { clip });
      drawImage(ctx, this.a, 'hud/hud_mp_vis_gun_title_right.png', x1 - 64, y0, 64, 64, { clip });
      // black at alpha 0.5, SmallItalic, centred in the tab (alignment ESTIMATE)
      this.a.text(ctx, 'SmallItalic', wh.name, (x0 + x1) / 2 - 1, y0 + 32 - 10, { color: [0, 0, 0], alpha: 0.5, align: 'center' });
      ctx.restore();
      if (fireA > 0) drawImage(ctx, this.a, wh.fireTypeIcon, R + right - 64, B + bottom - 47 - 64, 64, 64, { alpha: fireA });
    }
  }

  // ---- crosshair (T5 CG_DrawCrosshair / CG_DrawReticleSides / Center, ESTIMATE for T6) -------
  private drawCrosshair(ctx: CanvasRenderingContext2D, world: ClientWorld, refdef: RefDef): void {
    const ps = world.predicted;
    const ws = ps.weaponState;
    // the hatchet's reticle while it is out (offhand states)
    const throwing = ps.offHandIndex !== WeaponId.NONE && ws >= WeaponState.OFFHAND_INIT && ws <= WeaponState.OFFHAND_END;
    const weapon = throwing ? ps.offHandIndex : ps.weapon;
    if (weapon === WeaponId.NONE) return;
    // AllowedToDrawCrosshair: none while raising / dropping, reloading (T5 state 10) or in melee
    if (ws === WeaponState.RAISING || ws === WeaponState.RAISING_ALTSWITCH || ws === WeaponState.DROPPING
      || ws === WeaponState.DROPPING_QUICK || ws === WeaponState.RELOADING
      || ws === WeaponState.MELEE_INIT || ws === WeaponState.MELEE_FIRE || ws === WeaponState.MELEE_END) return;
    const wh = weaponHud(weapon);
    // ADS: none at posLerp 1 (keepCrosshairWhenADS 0 for every sas weapon); CG_TransitionToAds shrinks it
    // to half size over the last adsCrosshairInFrac (going in) / adsCrosshairOutFrac (coming out) of the lerp
    const ads = throwing ? 0 : ps.adsFrac;
    if (ads > this.lastAds + 1e-4) this.adsIn = true; else if (ads < this.lastAds - 1e-4) this.adsIn = false;
    this.lastAds = ads;
    if (ads >= 1) return;
    let trans = 1;
    if (ads > 0) {
      const frac = this.adsIn ? wh.adsCrosshairInFrac : wh.adsCrosshairOutFrac;
      const f = ads - (1 - frac);
      if (f > 0 && frac > 0) trans = 1 - (f / frac) * 0.5;
    }
    const cx = this.vp.vw / 2, cy = this.vp.vh / 2;
    const k = HUD640_SCALE; // 640x480 -> 720p
    const spreadScale = ps.aimSpreadScale / 255;
    // CG_CalcReticleColor: alpha * (1 - aimSpreadScale/255), min cg_crosshairAlphaMin
    const alpha = Math.max(CROSSHAIR_ALPHA_MIN, 1 - spreadScale);
    if (wh.reticleCenter) {
      const sz = wh.reticleCenterSize * k * trans;
      drawImage(ctx, this.a, wh.reticleCenter, cx - sz / 2, cy - sz / 2, sz, sz, { alpha });
    }
    if (wh.reticleSide) {
      const d = getWeaponDef(weapon as WeaponId);
      const stance = ps.viewHeightTarget <= 11 ? 2 : ps.viewHeightTarget <= 40 ? 1 : 0;
      const min = stance === 2 ? d.hipSpreadProneMin : stance === 1 ? d.hipSpreadDuckedMin : d.hipSpreadStandMin;
      const max = stance === 2 ? d.hipSpreadProneMax : stance === 1 ? d.hipSpreadDuckedMax : d.hipSpreadMax;
      // CG_CalcReticleSpread: f = lerp(min, max, aimSpreadScale/255) * transScale
      const spreadDeg = (min + (max - min) * spreadScale) * trans;
      const tanHalf = Math.tan((refdef.fovY * Math.PI) / 360);
      let scale = (240 * Math.tan((spreadDeg * Math.PI) / 180)) / tanHalf;
      if (wh.reticleMinOfs > scale) scale = wh.reticleMinOfs;
      const size = wh.reticleSideSize * k * trans;
      const spread = scale * k - wh.hipReticleSidePos * size;
      const img = wh.reticleSide;
      // top (0), right (90), bottom (180), left (270): each drawn size x size, rotated about its centre
      const pieces: [number, number, number][] = [
        [cx, cy - size / 2 - spread, 0], [cx + spread + size / 2, cy, 90], [cx, cy + spread + size / 2, 180], [cx - spread - size / 2, cy, 270],
      ];
      for (const [px, py, rot] of pieces) {
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate((rot * Math.PI) / 180);
        drawImage(ctx, this.a, img, -size / 2, -size / 2, size, size, { alpha });
        ctx.restore();
      }
    }
  }

  // ---- hitmarker (_damagefeedback.gsc) -------------------------------------------------------
  private drawHitmarker(ctx: CanvasRenderingContext2D, now: number): void {
    const t = now - this.hitmarkerTime;
    if (t < 0 || t >= HITMARKER_FADE_MS) return;
    const alpha = 1 - t / HITMARKER_FADE_MS;
    // 24x48 at (-12,-12) in 640x480; only the top half of the 64x128 image holds the X
    const k = HUD640_SCALE;
    const cx = this.vp.vw / 2, cy = this.vp.vh / 2;
    drawImage(ctx, this.a, 'hitmarker/damage_feedback.png', cx - 12 * k, cy - 12 * k, 24 * k, 48 * k, { alpha });
  }

  // ---- damage direction (T5 CG_DrawDamageDirectionIndicators, ESTIMATE) ----------------------
  private drawDamageArrows(ctx: CanvasRenderingContext2D, viewYaw: number, now: number): void {
    if (!this.damage.length) return;
    const k = HUD640_SCALE;
    const halfW = (128 * k) / 2, height = 64 * k, radius = 128 * k; // cg_hudDamageIconWidth/Height/Offset
    const cx = this.vp.vw / 2, cy = this.vp.vh / 2;
    for (let i = this.damage.length - 1; i >= 0; i--) {
      const d = this.damage[i]!;
      const t = now - d.time;
      if (t >= DAMAGE_ICON_MS) { this.damage.splice(i, 1); continue; }
      if (t < 0) continue;
      // alpha = clamp(2 - 2t/max, 0, 1): full for the first half, then fades
      const alpha = Math.min(1, 2 - (2 * t) / DAMAGE_ICON_MS);
      // dirYaw = world yaw from us toward the attacker; relative yaw (CCW +) -> screen angle
      const rel = ((d.yaw - viewYaw) * Math.PI) / 180;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-rel); // attacker to the left (rel > 0) puts the arc on the left
      drawImage(ctx, this.a, 'damage/hit_direction.png', -halfW, -radius - height, halfW * 2, height, { alpha });
      ctx.restore();
    }
  }

  // ---- low-health overlay (T5 CG_PulseLowHealthOverlay with T6 hud_healthOverlay_* dvars) ----
  private drawLowHealth(ctx: CanvasRenderingContext2D, health: number, maxHealth: number, dead: boolean, now: number): void {
    const ho = this.ho;
    const ratio = dead || maxHealth <= 0 ? 0 : Math.max(0, Math.min(1, health / maxHealth));
    if (ratio === 0) { ho.hurt = false; ho.toAlpha = 0; ho.fromAlpha = 0; ho.pulseDuration = 0; ho.oldHealth = 1; return; }
    const pulseMags = [1, 1, 1, 0.3];
    if (ho.oldHealth > ratio && HO.pulseStart > ratio) { ho.lastHitTime = now; ho.pulseIndex = 0; }
    ho.oldHealth = ratio;
    if (ho.pulseDuration + ho.pulseTime <= now) {
      if (HO.pulseStart > ratio || ho.hurt) {
        ho.hurt = true;
        ho.pulseTime = now;
        ho.fromAlpha = ho.toAlpha;
        if (ho.pulseIndex < 4) {
          const mag = pulseMags[ho.pulseIndex]!;
          if (ho.phase === 0) {
            ho.toAlpha = Math.max(0, Math.min(1, mag)); ho.pulseDuration = HO.phaseOneDur; ho.phase = 1;
          } else if (ho.phase === 1) {
            ho.toAlpha = Math.max(0, Math.min(1, mag * HO.phaseTwoMul)); ho.pulseDuration = HO.phaseTwoDur; ho.phase = 2;
          } else {
            ho.toAlpha = Math.max(0, Math.min(1, mag * HO.phaseThreeMul)); ho.pulseDuration = HO.phaseThreeDur; ho.phase = 0;
            if (now >= HO.regenPauseTime + ho.lastHitTime - 3 * (HO.phaseThreeDur + HO.phaseTwoDur + HO.phaseOneDur)) ho.pulseIndex++;
          }
        }
      }
      if (ratio > HO.pulseStop && ho.hurt) {
        ho.fromAlpha = this.hoAlpha(now);
        ho.hurt = false;
        ho.pulseTime = now;
        ho.toAlpha = HO.phaseEndTo;
        ho.pulseDuration = HO.phaseEndDur;
        ho.phase = 0;
      }
    }
    const alpha = this.hoAlpha(now);
    if (alpha > 0.001) drawImage(ctx, this.a, 'damage/overlay_low_health.png', 0, 0, this.vp.vw, this.vp.vh, { alpha });
  }

  private hoAlpha(now: number): number {
    const ho = this.ho;
    const t = Math.max(0, now - ho.pulseTime);
    if (ho.pulseDuration <= 0 || t >= ho.pulseDuration) return ho.toAlpha;
    return (ho.toAlpha - ho.fromAlpha) * (t / ho.pulseDuration) + ho.fromAlpha;
  }

  // ---- minimap (compass.lua AddMinimap) ------------------------------------------------------
  private drawMinimap(ctx: CanvasRenderingContext2D, world: ClientWorld, now: number): void {
    const sa = this.vp.safe;
    const box = anchorRect(sa, 'left', 13, 163, 'top', 7, 187);
    const map: Rect = { x: box.x, y: box.y, w: box.w, h: box.h - 30 }; // compass: bottom = -30
    const cx = map.x + map.w / 2, cy = map.y + map.h / 2;
    const half = map.w / 2;
    const pxPerUnit = half / COMPASS_MAX_RANGE;
    const me = world.view.origin;
    const yaw = world.view.viewangles[1];

    ctx.save();
    ctx.beginPath();
    ctx.rect(map.x, map.y, map.w, map.h);
    ctx.clip();
    // underlay (ESTIMATE: compass_map_color_underlay tiled behind the map)
    drawImage(ctx, this.a, 'compass/compass_map_color_underlay.png', map.x, map.y, map.w, map.h, { alpha: 0.8 });
    // map image, rotated around the player
    const def = MINIMAP_HIJACKED;
    const size = minimapWorldSize(def) * pxPerUnit;
    const uv = worldToMinimapUV(def, me[0], me[1]);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(compassRotation(yaw));
    drawImage(ctx, this.a, def.image, -uv[0] * size, -uv[1] * size, size, size);
    ctx.restore();
    // overlay frame
    drawImage(ctx, this.a, 'compass/mp_minimap_overlay.png', map.x, map.y, map.w, map.h);

    // forceRadar: permanent UAV, refreshed every 4 s, pings fade over 4 s
    if (this.radar.due(now)) {
      const top = topScorers(world.scoreboard);
      this.radarPings = world.players.filter((p) => p.alive && p.clientNum !== world.localClientNum)
        .map((p) => ({ x: p.origin[0], y: p.origin[1], first: top.has(p.clientNum) }));
      if (this.radarPings.length) this.radar.hadPings(); else this.radar.empty();
    }
    const pa = this.radar.pingAlpha(now);
    const off: [number, number] = [0, 0];
    const dot = 16 * 0.85; // ESTIMATE: enemy ping size inside the 150 px compass
    for (const p of this.radarPings) {
      compassOffset(p.x, p.y, me[0], me[1], yaw, pxPerUnit, off);
      // compassClampIcons 1: clamp to the edge of the radar
      const lim = half - dot / 2;
      const ox = Math.max(-lim, Math.min(lim, off[0])), oy = Math.max(-lim, Math.min(lim, off[1]));
      drawImage(ctx, this.a, p.first ? 'compass/compassping_firstplace.png' : 'compass/compassping_enemy.png', cx + ox - dot / 2, cy + oy - dot / 2, dot, dot, { alpha: pa });
    }
    ctx.restore();
    // the player arrow always points up (view direction); compassPlayerWidth 18.75 (T5 default, ESTIMATE)
    const pw = 18.75;
    drawImage(ctx, this.a, 'compass/compassping_player.png', cx - pw / 2, cy - pw / 2, pw, pw);

    // heading strip: T+155..T+181 (container bottom -32..-6), black 0.25 + 1 px white border 0.25
    const strip: Rect = { x: box.x, y: box.y + box.h - 32, w: box.w, h: 26 };
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.fillRect(strip.x, strip.y, strip.w, strip.h);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    ctx.strokeRect(strip.x + 0.5, strip.y + 0.5, strip.w - 1, strip.h - 1);
    const im = this.a.img('compass/hud_horizontal_compass_minimap.png');
    if (im) {
      const vis = HEADING_STRIP_VISIBLE; // fraction of the 360 deg strip that fits in the box
      const u0 = headingStripCenterU(yaw) - vis / 2;
      const iw = im.naturalWidth, ih = im.naturalHeight;
      // draw the visible window, wrapping around the texture
      let u = ((u0 % 1) + 1) % 1;
      let dx = strip.x;
      let remaining = vis;
      while (remaining > 1e-6) {
        const take = Math.min(remaining, 1 - u);
        const dw = (take / vis) * strip.w;
        ctx.drawImage(im, u * iw, 0, take * iw, ih, dx, strip.y, dw, strip.h);
        dx += dw; remaining -= take; u = 0;
      }
    }
  }

  // ---- obituaries ----------------------------------------------------------------------------
  private drawObituaries(ctx: CanvasRenderingContext2D, now: number): void {
    const lines = this.obits.layout(now, this.obitDraw);
    if (!lines.length) return;
    const sa = this.vp.safe;
    const win = anchorRect(sa, 'left', 13, 277, 'bottom', -249, -149);
    const lh = 20; // ExtraSmall 20
    ctx.save();
    ctx.beginPath();
    ctx.rect(win.x - 4, win.y - lh, win.w + 8, win.h + lh * 2);
    ctx.clip();
    for (const l of lines) {
      if (l.alpha <= 0) continue;
      const y = win.y + win.h - (l.slot + 1) * lh;
      let x = win.x;
      if (l.data.attacker) { x += this.a.text(ctx, 'ExtraSmall', l.data.attacker, x, y, { alpha: l.alpha }) + 4; }
      // icon 1.4 x line height, square (killIconRatio 1:1 for every sas weapon)
      const isz = lh * 1.4;
      drawImage(ctx, this.a, l.data.icon, x, y + lh / 2 - isz / 2, isz, isz, { alpha: l.alpha, flipX: l.data.flip });
      x += isz + 4;
      this.a.text(ctx, 'ExtraSmall', l.data.victim, x, y, { alpha: l.alpha });
    }
    ctx.restore();
  }

  // ---- obituary callout (notificationpopups.lua DisplayCallout / ObituaryState, nameplate.lua) --
  private drawCallout(ctx: CanvasRenderingContext2D, now: number): void {
    const c = this.callout;
    if (!c) return;
    const t = now - c.start;
    const SHOW = 2500, SLIDE = 150; // DisplayCallout(..., 2500, "obituary_callout"); in/out 150 ms
    if (t >= SHOW + SLIDE) { this.callout = null; return; }
    const W = 240, H = 60; // CoD.NamePlate.Width / Height
    const sa = this.vp.safe;
    const cy = sa.y + sa.h / 2;
    // "out" state: top = centre + 360 (ObitPlayerCallout_Y); "in": centre + 360 - 2 x 60
    const outTop = cy + 360, inTop = cy + 360 - 2 * H;
    const k = t < SLIDE ? t / SLIDE : t > SHOW ? 1 - (t - SHOW) / SLIDE : 1;
    const top = outTop + (inTop - outTop) * k;
    const left = sa.x + sa.w / 2 - W / 2;
    ctx.save();
    ctx.globalAlpha = 0.85; // CoD.NamePlate.Alpha
    // calling-card background (online-generated; emblem_bg_default is the engine's fallback image)
    drawImage(ctx, this.a, 'misc/emblem_bg_default.png', left, top, W, H);
    // player emblem 55 px (not extractable: SnS playlist icon stand-in)
    drawImage(ctx, this.a, 'wager/playlist_sticks_and_stones.png', left + 2, top + 2, H - 4, H - 4);
    this.a.text(ctx, 'ExtraSmall', c.name, left + 55, top + 8);
    this.a.text(ctx, 'ExtraSmall', c.text, left, top - 20);
    ctx.restore();
  }

  // ---- prematch countdown (_globallogic.gsc matchstarttimer) -------------------------------
  private drawPrematch(ctx: CanvasRenderingContext2D, world: ClientWorld, now: number): void {
    if (world.phase !== MatchPhase.PREMATCH && world.phase !== MatchPhase.WAITING) { this.prematchSec = -1; return; }
    const cx = this.vp.vw / 2, cy = this.vp.vh / 2;
    if (world.phase === MatchPhase.WAITING) {
      this.a.text(ctx, 'Default', STR.EXE_AWAITINGHOST, cx, cy - 40 * HUD640_SCALE - 12, { align: 'center', size: HUDELEM_PX * 1.5 });
      return;
    }
    const sec = Math.max(1, Math.ceil(world.timeLeftMs / 1000));
    if (sec !== this.prematchSec) { this.prematchSec = sec; this.prematchPulse = now; }
    // "objective" 1.5 at CENTER (0,-40): MATCH BEGINS IN
    this.a.text(ctx, 'Default', 'MATCH BEGINS IN', cx, cy - 40 * HUD640_SCALE - (HUDELEM_PX * 1.5) / 2, { align: 'center', size: HUDELEM_PX * 1.5 });
    // "big" 2.2, yellow (1,1,0), fontpulse: x2 over 1.5 frames (75 ms) then back over 3 frames (150 ms)
    const t = now - this.prematchPulse;
    const k = t < 75 ? 1 + t / 75 : t < 225 ? 2 - (t - 75) / 150 : 1;
    const size = HUDELEM_PX * 2.2 * k;
    this.a.text(ctx, 'Big', String(sec), cx, cy - size / 2, { align: 'center', size, color: [1, 1, 0] });
  }

  // ---- score popup + feed (scorepopup.lua, scorefeed.lua, additivetextoverlay.lua) ---------
  private drawScorePopup(ctx: CanvasRenderingContext2D, now: number): void {
    const p = this.scorePopup;
    const screen = this.vp.screen;
    if (p) {
      const t = now - p.start;
      const textW = this.a.measure('Condensed', p.text);
      const g = scorePopupGlow(t, textW);
      if (!g) this.scorePopup = null;
      else {
        // label: full-width box shifted 40 px right, y centre-75..-45, Condensed 30, centred
        const cx = screen.w / 2 + 40;
        const top = screen.h / 2 + SCORE_TEXT_TOP;
        const gcy = top + 30 / 2 - 5;
        // the label is added before the glow in AdditiveTextOverlay.newWithText, so the additive glow is on top
        this.a.text(ctx, 'Condensed', p.text, cx, top, { align: 'center' });
        drawImage(ctx, this.a, 'medal/hud_lui_scorefeed_glow.png', cx - g.outer.w / 2, gcy - g.outer.h / 2, g.outer.w, g.outer.h, { tint: g.outer.col });
        drawImage(ctx, this.a, 'medal/hud_lui_scorefeed_glow.png', cx - g.inner.w / 2, gcy - g.inner.h / 2, g.inner.w, g.inner.h, { tint: g.inner.col });
      }
    }
    // feed rows: safe-area centre +20..+220, from y centre-45 downwards, Dist font 25 px
    if (this.feed.length && now - this.feedLast >= FEED_DURATION_MS) this.feed = [];
    const sa = this.vp.safe;
    let y = sa.y + sa.h / 2 + SCORE_TEXT_TOP + 30;
    for (const r of this.feed) {
      const k = Math.min(1, (now - r.start) / FEED_EXPAND_MS);
      const h = FEED_ROW_H * k;
      if (k >= 1) this.a.text(ctx, 'Dist', r.text, sa.x + sa.w / 2 + 20, y, { size: FEED_ROW_H });
      y += h;
    }
  }

  // ---- medal (notificationpopups.lua MedalNotify) ---------------------------------------------
  private drawMedal(ctx: CanvasRenderingContext2D, now: number): void {
    if (!this.medal && this.medalQueue.length) {
      this.medal = { info: MEDALS[this.medalQueue.shift()!]!, start: now };
    }
    const m = this.medal;
    if (!m) return;
    const t = now - m.start;
    if (t >= MEDAL_TOTAL_MS) { this.medal = null; return; }
    const outStart = MEDAL_TOTAL_MS - MEDAL_OUT_MS;
    const a = t < MEDAL_IN_MS ? t / MEDAL_IN_MS : t > outStart ? 1 - (t - outStart) / MEDAL_OUT_MS : 1;
    const sa = this.vp.safe;
    const cx = sa.x + sa.w / 2, top = sa.y;
    ctx.save();
    ctx.globalAlpha = Math.max(0, a);
    // glow (AdditiveTextOverlay.new(70, 50, ...) with hud_lui_medal_glow), centred on the medal
    const gw = 70 * 2.5, gh = 50 * 4;
    drawImage(ctx, this.a, 'medal/hud_lui_medal_glow.png', cx - gw / 2, top + 64 - gh / 2 - 5, gw, gh, { tint: mix(WHITE, COLORS.scoreFeed1, Math.min(1, t / GLOW_PULSE_IN_MS)) });
    drawImage(ctx, this.a, m.info.image, cx - 64, top, 128, 128, { alpha: 0.9 });
    // bitchin_glint_reveal (500 ms shader shine, not extractable): a light sweep ESTIMATE
    if (t < 500) {
      const gx = cx - 64 + (t / 500) * 160 - 16;
      const grad = ctx.createLinearGradient(gx - 24, 0, gx + 24, 0);
      grad.addColorStop(0, 'rgba(255,255,255,0)'); grad.addColorStop(0.5, 'rgba(255,255,255,0.35)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = grad;
      ctx.fillRect(cx - 64, top, 128, 128);
      ctx.globalCompositeOperation = 'source-over';
    }
    // name: backing ks_menu_background 250 wide, y 128-25+3 .. 128+25+25+3; Default 25 offWhite + shadow
    drawImage(ctx, this.a, 'hud/ks_menu_background.png', cx - 125, top + 128 - 25 + 3, 250, 25 + 50);
    this.a.text(ctx, 'Default', m.info.name.toUpperCase(), cx, top + 128, { align: 'center', shadow: 0.5 });
    ctx.restore();
  }

  // ---- HUMILIATED / BANKRUPTED splash (see onGameEvent BANKRUPT note) -------------------------
  private drawSplash(ctx: CanvasRenderingContext2D, now: number): void {
    const s = this.splash;
    if (!s) return;
    const t = now - s.start;
    const total = 3000; // ESTIMATE (BO1 wager popup duration)
    if (t >= total) { this.splash = null; return; }
    const a = t < 200 ? t / 200 : t > total - 500 ? (total - t) / 500 : 1;
    const sa = this.vp.safe;
    const cx = sa.x + sa.w / 2, y = sa.y + 150;
    this.a.text(ctx, 'Big', s.title.toUpperCase(), cx, y, { align: 'center', alpha: a, color: COLORS.wagerOutOfTheMoney, shadow: 0.5 });
    this.a.text(ctx, 'Condensed', s.sub, cx, y + 50, { align: 'center', alpha: a, shadow: 0.5 });
  }

  // ---- killcam (ui_mp/t6/hud/killcam.lua) ------------------------------------------------------
  private drawKillcam(ctx: CanvasRenderingContext2D, world: ClientWorld, final: boolean): void {
    const vw = this.vp.vw, vh = this.vp.vh;
    const barH = 120; // KillcamRectHeight
    // KillcamTopRect / BottomRect: red (1,0,0) alpha 0.2; final killcam black alpha 0.2
    ctx.fillStyle = final ? 'rgba(0,0,0,0.2)' : 'rgba(255,0,0,0.2)';
    ctx.fillRect(0, 0, vw, barH);
    ctx.fillRect(0, vh - barH, vw, barH);
    // "KILLCAM" / "FINAL KILLCAM": Morris (60), alpha 0.75, at the bottom of the bottom bar (centred: ESTIMATE)
    this.a.text(ctx, 'Morris', final ? STR.MP_FINAL_KILLCAM_CAPS : STR.MP_KILLCAM_CAPS, vw / 2, vh - 60, { align: 'center', alpha: 0.75 });
    // nameplate of the killer above the text (CoD.NamePlate: name + kill icon; simplified)
    const kc = this.killcam;
    if (kc && kc.attacker >= 0) {
      const name = this.nameOf(world, kc.attacker);
      const icon = obituaryIcon(kc.weapon, 0, 0);
      const y = vh - barH + 6;
      const w = this.a.measure('Default', name) + 40;
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(vw / 2 - w / 2 - 8, y - 2, w + 16, 30);
      drawImage(ctx, this.a, icon.path, vw / 2 - w / 2, y, 26, 26, { flipX: icon.flip });
      this.a.text(ctx, 'Default', name, vw / 2 - w / 2 + 34, y);
    }
    if (!final) {
      // _killcam.gsc skip prompt: PLATFORM_PRESS_TO_SKIP with +activate (USE: square / F)
      const txt = STR.PLATFORM_PRESS_TO_SKIP.replace('^3[{+activate}]^7', 'SQUARE / F').replace(/\^\d/g, '');
      this.a.text(ctx, 'Default', txt, vw / 2, barH + 8, { align: 'center', alpha: 0.9, shadow: 0.5 });
    }
  }

  private drawConnectionInterrupted(ctx: CanvasRenderingContext2D, now: number): void {
    // T5 CG_DrawDisconnect: "Connection Interrupted", blinking (ESTIMATE position/blink)
    if (Math.floor(now / CONNECTION_INTERRUPTED_MS * 2) % 2) return;
    this.a.text(ctx, 'Default', STR.CGAME_CONNECTIONINTERUPTED, this.vp.vw / 2, 100 * HUD640_SCALE, { align: 'center', shadow: 0.5 });
  }

  // ---- scoreboard (scoreboard.lua) -------------------------------------------------------------
  private drawScoreboard(ctx: CanvasRenderingContext2D, world: ClientWorld): void {
    const screen = this.vp.screen;
    const rows = sortPlacement(world.scoreboard);
    const places = placesOf(rows);
    const w = 864, h = 460;
    const x = screen.w / 2 - w / 2, y = screen.h / 2 - h / 2;
    // header: 32 tall, black 0.7, white strip 0.06 at y 2..7; timer at +10 (Condensed), title at +80 (Default)
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(x, y, w, 32);
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(x, y + 2, w, 5);
    const secs = Math.ceil(Math.max(0, world.timeLeftMs) / 1000);
    this.a.text(ctx, 'Condensed', `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`, x + 10, y + 1);
    this.a.text(ctx, 'Default', `${STR.MPUI_SAS} - ${STR.MPUI_HIJACKED}`, x + 80, y + 4);
    // FFA block: grad stretch (black 0.5) + black 0.7 panel behind the rows
    const listY = y + 38;
    const listH = Math.max(1, rows.length) * 29 + 4;
    drawImage(ctx, this.a, 'menu/menu_mp_cac_grad_stretch.png', x, listY - 4, w, listH, { tint: [0, 0, 0], alpha: 0.5 });
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(x + 163, listY - 4, w - 163, listH);
    // column headers from x+500, 66 wide, ExtraSmall, alpha 0.5
    const cols = [STR.MPUI_SCORE, STR.MPUI_KILLS, STR.MPUI_DEATHS, STR.MPUI_TOMAHAWKS, STR.MPUI_HUMILIATED];
    for (let i = 0; i < cols.length; i++) {
      this.a.text(ctx, 'ExtraSmall', cols[i]!.toUpperCase(), x + 500 + i * 66 + 33, y + 6, { align: 'center', alpha: 0.5 });
    }
    // no PING header: scoreboard.lua only builds one for CoD.isPC + tu7_scoreboardPingAsNumbers, and even then it
    // adds an undefined element (f5_local8) instead of the text, so BO2 never shows it; the bars stand alone.
    // rows: 27 tall, 2 px gap, from y+38; span x+163..x+864
    let ry = listY;
    for (const r of rows) {
      const rx = x + 163;
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(rx, ry, w - 163, 7);
      // odd columns get an offWhite 0.2 background
      for (let i = 0; i < 5; i += 2) {
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.fillRect(rx + 337 + i * 66, ry, 66, 27);
      }
      const mine = r.clientNum === world.localClientNum;
      const col = mine ? COLORS.playerYellow : WHITE;
      this.a.text(ctx, 'ExtraSmall', String(places.get(r.clientNum) ?? 0), rx + 45, ry + 3, { align: 'right', color: col });
      const info = world.playerInfo.get(r.clientNum);
      this.a.text(ctx, 'Default', info?.name ?? `Player ${r.clientNum}`, rx + 78, ry + 1, { color: col });
      const vals = [r.points, r.kills, r.deaths, r.tomahawks, r.humiliated];
      for (let i = 0; i < vals.length; i++) this.a.text(ctx, 'ExtraSmall', String(vals[i]), rx + 337 + i * 66 + 33, ry + 3, { align: 'center', color: col });
      // ping bars 01..04: scoreboard.lua SCOREBOARD_PING_BARS[max(1, 4 - floor(ping / 100))]; bots show full bars
      const ping = info?.isBot ? 0 : r.ping;
      const bars = pingBars(ping);
      drawImage(ctx, this.a, `scoreboard/ping_bar_0${bars}.png`, rx + 670, ry, 27, 27);
      ry += 29;
    }
  }

  // ---- wager end screen (_hud_message.gsc wageroutcomenotify, hudelems in 640x480) -----------
  private drawWagerOutcome(ctx: CanvasRenderingContext2D, world: ClientWorld, t: number): void {
    const k = HUD640_SCALE;
    const cx = this.vp.vw / 2;
    const sa = this.vp.safe;
    const rows = sortPlacement(world.scoreboard);
    const pot = WAGER_DISPLAY_ANTE * rows.length;
    const pay = wagerPayouts(rows, pot);
    const mine = pay.get(world.localClientNum) ?? 0;
    const inMoney = mine > 0;
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(0, 0, this.vp.vw, this.vp.vh);
    // title: extrabig x3.0 at TOP +20, decode fx (200 ms per char reveal ESTIMATE)
    const titleSize = HUDELEM_PX * 3.0;
    const title = inMoney ? STR.MP_WAGER_IN_THE_MONEY_CAPS : STR.MP_WAGER_LOSS_CAPS;
    let y = sa.y + 20 * k;
    this.a.text(ctx, 'Morris', decodeFx(title, t), cx, y, { align: 'center', size: titleSize, color: inMoney ? COLORS.wagerInTheMoney : COLORS.wagerOutOfTheMoney });
    y += titleSize;
    // reason: objective x2.0
    const reasonSize = HUDELEM_PX * 2.0;
    this.a.text(ctx, 'Default', STR.MP_TIME_LIMIT_REACHED, cx, y, { align: 'center', size: reasonSize });
    y += reasonSize + 20 * 3 * k / 1.5;
    // rows: objective x1.5 at -175 (TOP_LEFT) and winnings right-aligned at +350; count up over 2 s
    const rowSize = HUDELEM_PX * 1.5;
    const first = pay.get(rows[0]?.clientNum ?? -1) ?? 0;
    const inc = Math.max(1, Math.trunc(first / (2 / 0.05)));
    const steps = Math.floor(t / 50);
    const left = cx - 175 * k;
    for (const r of rows) {
      const name = this.nameOf(world, r.clientNum);
      const target = pay.get(r.clientNum) ?? 0;
      const val = Math.min(target, steps * inc);
      const col: RGB = target > 0 ? COLORS.wagerInTheMoney : WHITE;
      this.a.text(ctx, 'Default', fmt('&&1', name), left, y, { size: rowSize, color: r.clientNum === world.localClientNum ? COLORS.playerYellow : WHITE });
      // MENU_POINTS "^BCP^ &&1": CoD Points font icon + value (icon drawn as the wager coin)
      const valText = String(val);
      const vw = this.a.text(ctx, 'Default', valText, left + 350 * k, y, { size: rowSize, color: col, align: 'right' });
      drawImage(ctx, this.a, target > 0 ? 'wager/wager_green.png' : 'wager/wager_yellow.png', left + 350 * k - vw - rowSize - 4, y + 2, rowSize - 4, rowSize - 4);
      y += rowSize + 20 * k / 1.5;
    }
  }
}

function mix(a: RGB, b: RGB, k: number): RGB {
  return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
}

/** setcod7decodefx(200, 60000, 600): letters resolve one by one (200 ms each) from random glyphs. */
export function decodeFx(text: string, t: number): string {
  const perChar = 200 / Math.max(1, text.length / 3); // ESTIMATE: whole title resolves in ~600 ms
  const shown = Math.floor(t / perChar);
  if (shown >= text.length) return text;
  const glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let out = text.slice(0, Math.max(0, shown));
  for (let i = Math.max(0, shown); i < text.length; i++) {
    const c = text[i]!;
    out += c === ' ' ? ' ' : glyphs[(Math.floor(t / 50) * 7 + i * 13) % glyphs.length];
  }
  return out;
}

export type { ScoreRow };
